import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import {
  readAnimalCompleteByAnimalId,
  unlinkAnimalFromContact,
} from "services/patients.service";
import ConfirmDialog from "components/ConfirmDialog";
import vgToast from "utils/toast";

type Row = {
  id: string;
  image: string;
  name: string;
  species: string;
};

function mapContactToRow(raw: unknown): Row {
  const row = raw as Record<string, unknown>;
  const first = String(row.contactFirstName ?? row.ContactFirstName ?? "").trim();
  const last = String(row.contactLastName ?? row.ContactLastName ?? "").trim();
  const full = String(row.contactFullName ?? row.ContactFullName ?? "").trim();
  const name =
    full || [first, last].filter(Boolean).join(" ") || "Unknown";
  return {
    id: String(row.contactId ?? row.ContactId ?? ""),
    image: String(row.cloudFileUrl ?? row.CloudFileUrl ?? "N/A"),
    name,
    species: String(row.contactType ?? row.ContactType ?? "N/A"),
  };
}

const ClientsList = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const animalId = id ? Number(id) : NaN;

  const [rows, setRows] = React.useState<Row[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [unlinkTarget, setUnlinkTarget] = React.useState<Row | null>(null);
  const [unlinking, setUnlinking] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgId <= 0 || !Number.isFinite(animalId) || animalId <= 0) {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await readAnimalCompleteByAnimalId(
        token,
        orgId,
        animalId
      );
      if (apiError) {
        setError(apiError.message);
        setRows([]);
        return;
      }
      setRows((data?.contactList ?? []).map(mapContactToRow));
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId, animalId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const requestUnlink = (contactId: string) => {
    const target = rows.find((r) => r.id === contactId) ?? null;
    if (!target) return;
    setUnlinkTarget(target);
  };

  const handleUnlink = async () => {
    if (!unlinkTarget) return;
    if (orgId <= 0 || !Number.isFinite(animalId) || animalId <= 0) return;
    const contactId = Number(unlinkTarget.id);
    if (!Number.isFinite(contactId) || contactId <= 0) return;

    setUnlinking(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await unlinkAnimalFromContact(
        token,
        orgId,
        contactId,
        animalId
      );
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      vgToast.success("Client unlinked.");
      setUnlinkTarget(null);
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setUnlinking(false);
    }
  };

  // Client-side paging: the API returns the whole collection.
  const view = useTableView(rows);

  return (
    <div>
      <PageSizeSelect
        value={view.pageSize}
        onChange={view.setPageSize}
        className="m-4"
      />

      <Table<Row>
        loading={loading}
        columns={{
          id: "Client Id",
          image: "Image",
          name: "Client Name",
          species: "Contact type",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        handleViewItem={(contactId) => navigate(`/app/clients/${contactId}`)}
        handleRemoveItem={requestUnlink}
        removeItemLabel="Unlink"
      />

      {loading && (
        <div className="px-4 pb-2 text-sm text-gray-500">Loading clients…</div>
      )}
      {error && <div className="px-4 pb-2 text-sm text-red-500">{error}</div>}

      <PaginationBar
        from={view.from}
        to={view.to}
        total={view.total}
        currentPage={view.page}
        totalPages={view.totalPages}
        onPrev={view.goPrev}
        onNext={view.goNext}
        onPageSelect={view.goToPage}
      />

      <ConfirmDialog
        open={!!unlinkTarget}
        title="Unlink client?"
        description={
          unlinkTarget
            ? `Remove "${unlinkTarget.name || `Client #${unlinkTarget.id}`}" from this patient. The client record itself stays intact.`
            : ""
        }
        confirmLabel="Unlink"
        busyLabel="Unlinking…"
        busy={unlinking}
        onCancel={() => {
          if (!unlinking) setUnlinkTarget(null);
        }}
        onConfirm={handleUnlink}
      />
    </div>
  );
};

export default ClientsList;
